/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import TargetMap from '../../actions/targetmap';
import { ACTION_CREATEOBJECT } from '../../store/state/actions';
import APP_CONSTANTS from '../../constants';
import Utils from '../../utils/utils';
import type { Action } from '../../store/state/state-manager';
import type { ActionsOut, DxAPIResponseObject } from '../../case/types';
import EnvironmentInfo from '../../environment-info';
import CaseUtils from '../../case/utils';
import { addActionToRemoveCurrentModalContainerItem } from '../../case/finish-assignment/helper';
import { addActionToRemoveContainerItem, isModalContainer } from '../../case/common-helper';
import { isContainerSingleMode } from '../../container/container-utils';
import { isAddContainerAction } from '../../store/state/epics/util';
import { publishDisplayCreatedCaseToasterEvent } from '../../case/create-work/events';
import { getLocaleUtils } from '../../locale/locale-utils';
import ObjectTransformer from '../../utils/object-transformer';

export const getContainerName = (createWorkActionPayload: Action['payload']) => {
  const { containerName, isInCreateStage } = createWorkActionPayload;
  if (isInCreateStage) {
    return APP_CONSTANTS.MODAL;
  }
  return containerName || TargetMap.getTargetViewContainerName(ACTION_CREATEOBJECT);
};

export const isInCreateStage = (dxApiResponse: DxAPIResponseObject) =>
  dxApiResponse?.uiResources?.root?.config?.name === 'pzCreateDetails' || false;

export const setDirectUrlUpdation = (
  dxApiResponse: DxAPIResponseObject,
  createWorkActionPayload: Action['payload']
) => {
  const isInCreateStageRes = isInCreateStage(dxApiResponse);
  const { skipBrowserSemanticUrlUpdate = false } = createWorkActionPayload;
  // URL and title updation should be done only for non create stages.
  Utils.setIn(dxApiResponse, ['directUrlUpdation'], !isInCreateStageRes && !skipBrowserSemanticUrlUpdate);
};

export const getCaseViewMode = (dxApiResponse: DxAPIResponseObject) => {
  if (dxApiResponse.confirmationNote) {
    return APP_CONSTANTS.REVIEW_MODE;
  }
  return APP_CONSTANTS.PERFORM_MODE;
};

export const isCreateStageActive = (actionInPayload: any, response: DxAPIResponseObject) => {
  let isInCreateStageRes;
  if (actionInPayload.renderInline || EnvironmentInfo.getDefaultCasePage()) {
    isInCreateStageRes = false;
  } else {
    isInCreateStageRes = isInCreateStage(response);
  }
  return isInCreateStageRes;
};

export const addActionsForExternalObject = (actionInPayload: Action['payload'], actionsOut: ActionsOut[]) => {
  if (CaseUtils.isExternalObject(actionInPayload.class)) {
    actionInPayload.containerItemID = actionInPayload.context;
    addActionToRemoveCurrentModalContainerItem(actionsOut, actionInPayload);
  }
};

export const canOpenCaseViewAfterCreate = (openCaseViewRes: boolean, isInCreateStageRes: boolean) =>
  !openCaseViewRes && !isInCreateStageRes;

export const canContinueExecution = (containerName: string, target: string | null, className: string) =>
  !isModalContainer(containerName) && isContainerSingleMode(target) && !CaseUtils.isExternalObject(className);

export const addActionBasedOnContainerMode = (
  actions: Action,
  options: { removeActionPayload: Action['payload']; target: string | null },
  actionsOut: ActionsOut[]
) => {
  if (isAddContainerAction(actions)) {
    const { removeActionPayload, target } = options;
    addActionToRemoveContainerItem(actionsOut, { removeActionPayload, target });
  }
};

export const triggerExternalObjectSubmitEvent = (actionInPayload: Action['payload']) => {
  if (CaseUtils.isExternalObject(actionInPayload.class)) {
    publishDisplayCreatedCaseToasterEvent({
      content: getLocaleUtils().getLocaleValue('Successfully submitted!', 'Assignment')
    });
  }
};

export const resetMappingFlagOnExternalObjectCreation = (className: string) => {
  if (CaseUtils.isExternalObject(className)) {
    ObjectTransformer.setNeedsMapping(false);
  }
};
